<template>
  <div class="initialuw-trends-shell" :style="layoutDockStyle">
    <div class="initialuw-trends">
      <div class="initialuw-trends__grid">
        <div class="left-stack">
          <initial-uw-top-summary-card :rows="filteredRows"
                                       :rows-loading="rowsLoading"
                                       :resubmittals-rows="filteredResubmittalsRows"
                                       :resubmittals-loading="resubmittalsLoading"
                                       :resubmittals-error="resubmittalsError"
                                       :underwrites-resubs-rows="filteredUnderwritesResubsRows"
                                       :underwrites-resubs-loading="underwritesResubsLoading"
                                       :underwrites-resubs-error="underwritesResubsError"
                                       @openResubmittalsOverBizHours="openResubmittalsOverBizHours"
                                       @openUnderwritesResubs="openUnderwritesResubs"
                                       @openNewUnderwrites="openConditionMain('New')"
                                       @openConditions="openNeedsAttention" />

          <div class="trend-card__subgrid" :class="{ 'trend-card__subgrid--fill': !isCorp }">
            <InitialUwRecentNotesCard :azureId="azureId"
                                      :authToken="authToken"
                                      :orgId="orgId"
                                      :lastUpdatedAt="lastUpdated.notes"
                                      @refreshed="markUpdated('notes')"
                                      @openLoan="openLoanTasks" />

            <InitialUwRecentLoansCard :azureId="azureId"
                                      :authToken="authToken"
                                      :lastUpdatedAt="lastUpdated.recentLoans"
                                      @refreshed="markUpdated('recentLoans')"
                                      @openLoan="openLoanTasks" />
          </div>
        </div>

        <div class="initialuw-right-stack">
          <InitialUwLeaderboardCard :azureId="azureId"
                                    :authToken="authToken"
                                    :orgId="orgId"
                                    :lastUpdatedAt="lastUpdated.leaderboard"
                                    :nextRefreshAt="nextRefresh.leaderboard"
                                    @refreshed="markUpdated('leaderboard')" />
        </div>
      </div>
    </div>

    <!-- <div v-if="!isAIDrawerOpen" class="uw-swiftai-fab" @click="openAIDrawer">
      <i class="fa-duotone fa-wand-magic-sparkles"></i>
      SwiftChat
    </div> -->

    <div v-if="isAIDrawerOpen && viewportWidth > 925"
         class="uw-dock-overlay"
         @click="isAIDrawerOpen = false" />

    <div v-if="isAIDrawerOpen && viewportWidth > 925"
         class="uw-ai-dock open"
         :style="{ width: aiDockWidth + 'px' }"
         @click.stop>
      <div class="uw-ai-resize-handle" @mousedown="startResize"></div>

      <AIFlyoutDrawer :open="true"
                      :width="aiDockWidth"
                      :tabs="AITabs"
                      :selected-tab="selectedTab"
                      :uw-guides-url="uwGuidesUrl"
                      :doc-analysis-url="docAnalysisUrl"
                      :pipeline-url="pipelineUrl"
                      :productFinder-url="productFinderUrl"
                      @close="isAIDrawerOpen = false"
                      @tab-selected="onTabSelected" />
    </div>

    <AIFlyoutDrawer v-if="isAIDrawerOpen && viewportWidth <= 925"
                    :open="true"
                    :width="aiDockWidth"
                    :tabs="AITabs"
                    :selected-tab="selectedTab"
                    :uw-guides-url="uwGuidesUrl"
                    :doc-analysis-url="docAnalysisUrl"
                    :productFinder-url="productFinderUrl"
                    @close="isAIDrawerOpen = false"
                    @tab-selected="onTabSelected" />

    <Teleport to=".dashboard-header">
      <div class="initialuw-scope-pill">
        <i class="fa-duotone fa-sitemap"></i>
        <span class="initialuw-scope-pill-text">Showing</span>
        <k-switch
          class="initialuw-scope-switch"
          :onLabel="'All'"
          :offLabel="'Mine'"
          :checked="!mineOnly"
          @change="onScopeToggle"
        />
      </div>
    </Teleport>
  </div>
</template>

<script>
  import InitialUwTopSummaryCard from "./InitialUwTopSummaryCard.vue"
  import InitialUwRecentNotesCard from "./InitialUwRecentNotesCard.vue"
  import InitialUwRecentLoansCard from "./InitialUwRecentLoansCard.vue"
  import InitialUwLeaderboardCard from "./InitialUwLeaderboardCard.vue"

  import { Switch } from "@progress/kendo-vue-inputs"
  import SignalRService from "@/api/signalr.service"
  import AIFlyoutDrawer from "@/components/commandCenter/AIFlyoutDrawer.vue"
  import ConditionsApi from "@/api/api.service"

  export default {
    name: "ConditionsInitialUWTrends",
    components: {
      InitialUwTopSummaryCard,
      InitialUwRecentNotesCard,
      InitialUwRecentLoansCard,
      InitialUwLeaderboardCard,
      AIFlyoutDrawer,
      "k-switch": Switch
    },
    props: {
      azureId: { type: String, required: true },
      authToken: { type: String, required: true },
      orgId: { type: [Number, String], default: null },
      rows: { type: Array, default: () => [] },
      rowsLoading: { type: Boolean, default: false }
    },
    data() {
      const w = typeof window !== "undefined" ? window.innerWidth : 0
      const h = typeof window !== "undefined" ? window.innerHeight : 0

      return {
        viewportWidth: w,
        viewportHeight: h,

        resubmittalsRows: [],
        resubmittalsLoading: false,
        resubmittalsError: null,

        underwritesResubsRows: [],
        underwritesResubsLoading: false,
        underwritesResubsError: null,

        _needsAttentionBound: false,

        _refreshTimer: null,
        _tickTimer: null,
        _timerRefreshMs: 1000 * 60 * 10,

        lastUpdated: {
          topSummary: null,
          chart: null,
          leaderboard: null,
          notes: null,
          recentLoans: null
        },

        nextRefresh: {
          chart: null,
          leaderboard: null
        },

        aiDockWidth: 420,
        aiDockMinWidth: 340,
        aiDockMaxWidth: 9999,
        _isResizingDock: false,

        isAIDrawerOpen: false,
        _didInitDockWidths: false,

        AITabs: [
          { title: "Search UW Guidelines", content: "UWGuides" },
          { title: "Document Analyzer", content: "docsum" },
          { title: "Pipeline Insights", content: "pipeline" },
          { title: "Product Finder", content: "product_finder" }
        ],
        selectedTab: 0,

        mineOnly: localStorage.getItem("uw_dashboard_scope") === "mine"
      }
    },
    computed: {
      roles() {
        const raw = localStorage.getItem("active_user_roles") || localStorage.getItem("user_roles")
        if (!raw) return []
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            return parsed.map(x => String(x || "").toLowerCase().trim()).filter(Boolean)
          }
          return String(parsed || "")
            .split(",")
            .map(x => String(x || "").toLowerCase().trim())
            .filter(Boolean)
        } catch {
          return String(raw || "")
            .split(",")
            .map(x => String(x || "").toLowerCase().trim())
            .filter(Boolean)
        }
      },
      isUW() {
        return this.roles.includes("underwriter")
      },
      isUWSupport() {
        return this.roles.includes("uwsupport")
      },
      isCorp() {
        return this.roles.includes("corp") && !this.isUW && !this.isUWSupport
      },
      layoutDockStyle() {
        return {}
      },
      uwGuidesUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/chat/gpt/iframe/`
      },
      docAnalysisUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/chat/summary`
      },
      pipelineUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/chat/gpt/iframe/pipeline`
      },
      productFinderUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/iframe/productFinder`
      },

      filteredRows() {
        if (!this.mineOnly) return Array.isArray(this.rows) ? this.rows : []
        const id = this.azureId
        return (Array.isArray(this.rows) ? this.rows : []).filter(r =>
          r.underwriterAzureId === id
        )
      },

      filteredResubmittalsRows() {
        if (!this.mineOnly) return this.resubmittalsRows
        const id = this.azureId
        return this.resubmittalsRows.filter(r => r.underwriterAzureId === id)
      },

      filteredUnderwritesResubsRows() {
        if (!this.mineOnly) return this.underwritesResubsRows
        const id = this.azureId
        return this.underwritesResubsRows.filter(r => r.underwriterAzureId === id)
      }
    },

    watch: {
      authToken() {
        this.fetchResubmittalsOverBizHours()
        this.fetchUnderwritesResubs()
      },
      azureId() {
        this.fetchResubmittalsOverBizHours()
        this.fetchUnderwritesResubs()
      }
    },

    methods: {
      onScopeToggle() {
        this.mineOnly = !this.mineOnly
        try {
          localStorage.setItem("uw_dashboard_scope", this.mineOnly ? "mine" : "all")
        } catch (e) {}
      },

      async fetchResubmittalsOverBizHours() {
        if (!this.authToken || !this.azureId) return

        this.resubmittalsLoading = true
        this.resubmittalsError = null

        try {
          const resp = await ConditionsApi.GetResubmittalsOverBizHours(this.authToken, this.azureId)
          const payload = resp?.data
          this.resubmittalsRows = Array.isArray(payload?.data) ? payload.data : []
        } catch {
          this.resubmittalsRows = []
          this.resubmittalsError = "Could not load resubs outside of SLA"
        } finally {
          this.resubmittalsLoading = false
        }
      },

      async fetchUnderwritesResubs() {
        if (!this.authToken || !this.azureId) return

        this.underwritesResubsLoading = true
        this.underwritesResubsError = null

        try {
          const resp = await ConditionsApi.GetUnderwritesResubs(this.authToken, this.azureId)
          const payload = resp?.data
          this.underwritesResubsRows = Array.isArray(payload?.data) ? payload.data : []
        } catch {
          this.underwritesResubsRows = []
          this.underwritesResubsError = "Could not load underwrites/resubs"
        } finally {
          this.underwritesResubsLoading = false
        }
      },

      openResubmittalsOverBizHours(payload) {
        this.$emit("openResubmittalsOverBizHours", payload)
      },

      openUnderwritesResubs(payload) {
        this.$emit("openUnderwritesResubs", payload)
      },

      _updateViewport() {
        if (typeof window === "undefined") return
        this.viewportWidth = window.innerWidth
        this.viewportHeight = window.innerHeight
      },

      _initDockWidthIfNeeded() {
        if (this._didInitDockWidths) return
        this._didInitDockWidths = true

        const vw = this.viewportWidth || window.innerWidth
        const target = Math.round(vw * 0.33)

        const saved = parseInt(localStorage.getItem("initialuw_ai_dock_width"), 10)
        if (!isNaN(saved)) {
          this.aiDockWidth = saved
          return
        }

        const initial = Math.max(this.aiDockMinWidth, Math.min(this.aiDockMaxWidth, target))
        this.aiDockWidth = initial
      },

      openAIDrawer() {
        this._updateViewport()
        this._initDockWidthIfNeeded()
        this.isAIDrawerOpen = true
      },

      onTabSelected(selected) {
        this.selectedTab = selected
      },

      startResize() {
        if (!this.isAIDrawerOpen) return
        this._isResizingDock = true

        document.body.classList.add("uw-resize-grabbing")
        window.addEventListener("mousemove", this.onResizeMove, { passive: false })
        window.addEventListener("mouseup", this.stopResize, { passive: true })
      },

      onResizeMove(e) {
        if (!this._isResizingDock) return
        e.preventDefault()

        const maxAllowed = Math.min(this.aiDockMaxWidth, this.viewportWidth - 24)
        const proposed = this.viewportWidth - e.clientX

        this.aiDockWidth = Math.max(this.aiDockMinWidth, Math.min(maxAllowed, proposed))
      },

      stopResize() {
        if (!this._isResizingDock) return

        this._isResizingDock = false
        document.body.classList.remove("uw-resize-grabbing")

        window.removeEventListener("mousemove", this.onResizeMove)
        window.removeEventListener("mouseup", this.stopResize)

        localStorage.setItem("initialuw_ai_dock_width", String(this.aiDockWidth))
      },

      openConditionMain(loanType) {
        const query = { loanType, mode: "newunderwrites" }
        const route = this.$router.resolve({ name: "ConditionMain", query })
        window.open(route.href, "_blank", "noopener,noreferrer")
      },

      openNeedsAttention() {
        const query = {
          mode: "conditions",
          needsAttention: "true",
          conditionType: "Underwriting"
        }
        const route = this.$router.resolve({ name: "ConditionMain", query })
        window.open(route.href, "_blank", "noopener,noreferrer")
      },

      openLoanTasks(loanId, condId = null) {
        if (!loanId) return
        const isDev =
          window.location.hostname === "localhost" ||
          window.location.hostname === "dev.swiftapp.ai"
        const baseUrl = isDev
          ? "https://dev-swift.goldstarfinancial.com"
          : "https://swift.goldstarfinancial.com"
        window.open(`${baseUrl}?id=${loanId}${condId ? `&condid=${condId}` : ""}`, "_blank")
      },

      markUpdated(key) {
        this.lastUpdated = { ...this.lastUpdated, [key]: new Date().toISOString() }
      },

      markTopSummaryUpdated() {
        this.markUpdated("topSummary")
      },

      async subscribeNeedsAttentionRealtime() {
        if (this._needsAttentionBound) return
        this._needsAttentionBound = true

        await SignalRService.startConditionsConnection()

        SignalRService.onNeedsAttentionChanged(dto => {
          this.markTopSummaryUpdated()
          this.$emit("needsAttentionChanged", dto)
        })
      },

      scheduleNextTimerTargets() {
        const next = new Date(Date.now() + this._timerRefreshMs).toISOString()
        this.nextRefresh = {
          ...this.nextRefresh,
          chart: next,
          leaderboard: next
        }
      },

      startTimerRefresh() {
        clearInterval(this._refreshTimer)
        this.scheduleNextTimerTargets()

        this._refreshTimer = setInterval(() => {
          this.scheduleNextTimerTargets()

          this.$emit("refreshLeaderboardRequested", { reason: "timer", ts: Date.now() })
          this.$emit("refreshChartRequested", { reason: "timer", ts: Date.now() })

          this.fetchResubmittalsOverBizHours()
          this.fetchUnderwritesResubs()
        }, this._timerRefreshMs)
      },

      startTickTimer() {
        clearInterval(this._tickTimer)
        this._tickTimer = setInterval(() => {
          this.$forceUpdate()
        }, 1000)
      },

      stopTimers() {
        clearInterval(this._refreshTimer)
        clearInterval(this._tickTimer)
        this._refreshTimer = null
        this._tickTimer = null
      }
    },

    async mounted() {
      const savedWidth = parseInt(localStorage.getItem("initialuw_ai_dock_width"), 10)
      if (!isNaN(savedWidth)) this.aiDockWidth = savedWidth

      this._updateViewport()
      window.addEventListener("resize", this._updateViewport, { passive: true })

      this.markUpdated("notes")
      this.markUpdated("recentLoans")
      this.markTopSummaryUpdated()

      await this.fetchResubmittalsOverBizHours()
      await this.fetchUnderwritesResubs()
      await this.subscribeNeedsAttentionRealtime()

      this.startTimerRefresh()
      this.startTickTimer()
    },

    beforeUnmount() {
      this._needsAttentionBound = false
      this.stopTimers()

      window.removeEventListener("mousemove", this.onResizeMove)
      window.removeEventListener("mouseup", this.stopResize)
      document.body.classList.remove("uw-resize-grabbing")
      window.removeEventListener("resize", this._updateViewport)
    }
  }
</script>


<style scoped>
  .initialuw-trends-shell {
    width: 100%;
  }

  .initialuw-right-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .initialuw-scope-pill {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.1);
    background: #fff;
    font-weight: 900;
    font-size: 12px;
    color: rgba(15, 23, 42, 0.7);
  }

  .initialuw-scope-pill-text {
    margin-right: 2px;
  }

  .initialuw-scope-switch {
    width: 67px;
  }

  .initialuw-trends {
    background: var(--background);
    border-bottom: 1px solid var(--border-ltr);
  }

  .initialuw-trends__grid {
    display: grid;
    grid-template-columns: 1fr 572px;
    gap: 12px;
    align-items: start;
  }

  .left-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: calc(100vh - 94px);
    min-height: 0;
    overflow: hidden;
  }

  .trend-card__subgrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 0;
    border-top: none;
    background: transparent;
    flex: 1;
    min-height: 0;
  }

    .trend-card__subgrid .trend-card {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

  .uw-swiftai-fab {
    position: fixed;
    right: 18px;
    bottom: 18px;
    z-index: 9999;
    height: 54px;
    padding: 0 18px;
    border-radius: 18px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    border: 1px solid rgba(37, 99, 235, 0.32);
    background: var(--primary);
    cursor: pointer;
    user-select: none;
    font-weight: 950;
    font-size: 14px;
    color: #fff;
    letter-spacing: 0.2px;
    transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
  }

    .uw-swiftai-fab:hover {
      transform: translateY(-2px);
      box-shadow: 0 22px 40px rgba(37, 99, 235, 0.32);
      filter: brightness(1.05);
    }

    .uw-swiftai-fab:active {
      transform: translateY(0px) scale(0.98);
    }

    .uw-swiftai-fab i {
      font-size: 16px;
      --fa-primary-color: #1d4ed8;
      --fa-secondary-color: #fde68a;
      --fa-secondary-opacity: 1;
      filter: drop-shadow(0 10px 22px rgba(37, 99, 235, 0.35)) drop-shadow(0 6px 16px rgba(251, 191, 36, 0.35));
    }

  .uw-ai-dock {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    z-index: 15000;
    background: #fff;
    border-left: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow: -18px 0 34px rgba(15, 23, 42, 0.12);
    display: flex;
    flex-direction: column;
  }

  .uw-ai-resize-handle {
    position: absolute;
    left: -6px;
    top: 0;
    width: 12px;
    height: 100%;
    cursor: ew-resize;
    z-index: 15001;
  }

  .uw-resize-grabbing {
    cursor: ew-resize !important;
    user-select: none !important;
  }

  .uw-dock-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.18);
    backdrop-filter: blur(1px);
    z-index: 14999;
  }

  @media (max-width: 1200px) {
    .initialuw-trends__grid {
      grid-template-columns: 1fr;
    }

    .trend-card__subgrid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 925px) {
    .uw-swiftai-fab {
      right: 14px;
      bottom: 14px;
    }
  }

  .uw-ai-resize-handle {
    background: linear-gradient( to right, rgba(15, 23, 42, 0.12), rgba(15, 23, 42, 0) );
    transition: background 0.15s ease, opacity 0.15s ease;
    opacity: 0.75;
    left: -10px;
    width: 30px;
  }

    .uw-ai-resize-handle:hover {
      opacity: 1;
      background: linear-gradient( to right, rgba(37, 99, 235, 0.22), rgba(37, 99, 235, 0) );
    }

    .uw-ai-resize-handle::after {
      content: "";
      position: absolute;
      left: 50%;
      top: 50%;
      width: 4px;
      height: 34px;
      transform: translate(-50%, -50%);
      border-radius: 99px;
      background: rgba(15, 23, 42, 0.22);
      box-shadow: 0 -10px 0 rgba(15, 23, 42, 0.18), 0 10px 0 rgba(15, 23, 42, 0.18);
    }

  .uw-ai-dock:hover .uw-ai-resize-handle {
    opacity: 1;
  }
</style>